import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Estimate, useCreateRevision } from "@/hooks/useEstimates";
import { toast } from "sonner";
import { GitBranch, FileText } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimate: Estimate;
  onCreated: (newId: string) => void;
}

interface PlanDoc {
  id: string;
  file_name: string;
}

export function ReviseEstimateDialog({ open, onOpenChange, estimate, onCreated }: Props) {
  const [notes, setNotes] = useState("");
  const [planDocId, setPlanDocId] = useState<string>("");
  const [planDocs, setPlanDocs] = useState<PlanDoc[]>([]);
  const createRevision = useCreateRevision();

  useEffect(() => {
    if (!open) return;
    setNotes("");
    setPlanDocId("");
    // Fetch plan documents for this estimate
    const rootId = estimate.parent_estimate_id ?? estimate.id;
    supabase
      .from("estimate_documents" as any)
      .select("id, file_name")
      .or(`estimate_id.eq.${estimate.id},estimate_id.eq.${rootId}`)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setPlanDocs((data as any[] ?? []).map((d: any) => ({ id: d.id, file_name: d.file_name })));
      });
  }, [open, estimate.id, estimate.parent_estimate_id]);

  const handleCreate = async () => {
    try {
      const result = await createRevision.mutateAsync({
        sourceEstimate: estimate,
        revisionNotes: notes,
        referencePlanDocumentId: planDocId || null,
      });
      toast.success(`Revision ${(result as any).revision_number} created`);
      onOpenChange(false);
      onCreated((result as any).id);
    } catch (err: any) {
      toast.error(err.message || "Failed to create revision");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" /> Create Revision
          </DialogTitle>
          <DialogDescription>
            Creates a new draft copy (Rev {(estimate.revision_number ?? 0) + 1}) based on the current estimate. The original is preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="rev-notes">What changed in this revision?</Label>
            <Textarea
              id="rev-notes"
              placeholder="e.g. Updated material quantities per revised plan set…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {planDocs.length > 0 && (
            <div className="space-y-2">
              <Label>Reference Plan Set (optional)</Label>
              <Select value={planDocId} onValueChange={setPlanDocId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan document…" />
                </SelectTrigger>
                <SelectContent>
                  {planDocs.map((doc) => (
                    <SelectItem key={doc.id} value={doc.id}>
                      <span className="flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        {doc.file_name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={createRevision.isPending}>
            {createRevision.isPending ? "Creating…" : "Create Revision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
